/**
 * F1's "Users by Role" — who the people on this platform are.
 *
 * 🔴 IT WAS A DONUT AND IT IS A BAR NOW, FOR TWO REASONS THAT ARE BOTH ABOUT
 * BEING READ CORRECTLY.
 *
 * The first is arithmetic. The donut drew two arcs — students and teachers —
 * while the number in its middle counted three roles, so the ring never closed:
 * a real platform of 44 students, 5 teachers and 3 parents rendered as 84.6% +
 * 9.6% and an unexplained grey gap, with a legend summing to 49 under a centre
 * reading 52. A chart whose parts do not add up to its own total is worse than
 * no chart, because the gap looks like a rendering artefact rather than a
 * missing category.
 *
 * The second is shape. This split is heavily skewed — one category is most of
 * the platform and the other two are a few per cent each. On a ring those two
 * are slivers a few pixels of arc long, and a reader compares them by angle,
 * which is the thing people are worst at. As segments of one bar they are
 * compared by length, which is the thing people are best at, and a 5.8% share
 * of a full-width bar is a segment you can actually see.
 *
 * ⚠️ NO MINIMUM SEGMENT WIDTH. A floor would make a 0.2% role look like a 2%
 * one — the exact misreading this component exists to stop. A share too small to
 * draw is still printed as a number in the row beneath, which is where an
 * operator reads the value anyway.
 */

export interface RoleCount {
  key: string
  label: string
  count: number
  /** Tailwind background class for the swatch and the bar segment — one source for both. */
  tint: string
}

export interface RoleSplitProps {
  roles: readonly RoleCount[]
  /** What the roles are drawn against. Passed in rather than summed here, so the caller decides what "total" means. */
  total: number
  /** Rendered when nothing has been counted yet. */
  emptyMessage?: string
}

/** Share of the total, as a percentage. `0` when there is nothing to divide by — never `NaN`. */
export function shareOf(count: number, total: number): number {
  return total > 0 ? (count / total) * 100 : 0
}

/**
 * One decimal, and only when it says something.
 *
 * ⚠️ "9.6%" AND "10%", NOT "9.6%" AND "10.0%". A trailing zero on a whole number
 * implies a precision the count does not have — these are headcounts, and five
 * teachers out of fifty-two is exactly a tenth of the platform.
 */
export function formatShare(pct: number): string {
  return `${Number.isInteger(pct) ? String(pct) : pct.toFixed(1)}%`
}

export function RoleSplit({ roles, total, emptyMessage = 'Nobody on the platform yet.' }: RoleSplitProps) {
  if (total <= 0) {
    return (
      <p className="flex h-32 items-center justify-center text-center font-dash text-dash-xs font-semibold text-muted">
        {emptyMessage}
      </p>
    )
  }

  const counted = roles.reduce((sum, r) => sum + r.count, 0)
  const other = Math.max(0, total - counted)

  const segments = [
    ...roles,
    ...(other > 0 ? [{ key: '__other', label: 'School Admins', count: other, tint: 'bg-amber-500', hex: '#f59e0b' }] : []),
  ]

  // Color mapping hex fallback
  const getHex = (tint: string, hex?: string) => {
    if (hex) return hex
    if (tint.includes('emerald') || tint.includes('green')) return '#22c55e'
    if (tint.includes('blue') || tint.includes('sky')) return '#3b82f6'
    if (tint.includes('purple') || tint.includes('violet')) return '#a855f7'
    if (tint.includes('amber') || tint.includes('orange')) return '#f97316'
    return '#64748b'
  }

  // Calculate SVG donut arcs
  const radius = 64
  const circumference = 2 * Math.PI * radius
  let accumulatedOffset = 0

  const donutArcs = segments.map((s) => {
    const pct = shareOf(s.count, total)
    const strokeDasharray = `${((pct / 100) * circumference).toFixed(2)} ${circumference.toFixed(2)}`
    const strokeDashoffset = (-accumulatedOffset).toFixed(2)
    accumulatedOffset += (pct / 100) * circumference
    return {
      ...s,
      pct,
      hex: getHex(s.tint, (s as { hex?: string }).hex),
      strokeDasharray,
      strokeDashoffset,
    }
  })

  return (
    <div className="flex flex-col gap-5">
      {/* SVG Donut Chart */}
      <div className="relative flex items-center justify-center my-2">
        <svg className="h-44 w-44 -rotate-90 transform overflow-visible" viewBox="0 0 160 160">
          <circle
            cx="80"
            cy="80"
            r={radius}
            fill="none"
            stroke="#f1f5f9"
            strokeWidth="18"
          />
          {donutArcs.map((arc) => (
            <circle
              key={arc.key}
              cx="80"
              cy="80"
              r={radius}
              fill="none"
              stroke={arc.hex}
              strokeWidth="18"
              strokeDasharray={arc.strokeDasharray}
              strokeDashoffset={arc.strokeDashoffset}
              strokeLinecap="round"
              className="transition-all duration-500 ease-out"
            />
          ))}
        </svg>

        {/* Donut Center Label */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
          <span className="font-dash text-dash-2xl font-black text-slate-800 tabular leading-none">
            {total.toLocaleString()}
          </span>
          <span className="text-[11px] font-bold text-slate-400 mt-1 uppercase tracking-wider">
            Total Users
          </span>
        </div>
      </div>

      {/* Legend list */}
      <ul className="flex flex-col gap-2.5">
        {donutArcs.map((s) => (
          <li key={s.key} className="flex items-center justify-between gap-3 text-dash-xs">
            <span className="flex min-w-0 items-center gap-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.hex }} />
              <span className="truncate font-bold text-slate-700">{s.label}</span>
            </span>
            <span className="shrink-0 font-extrabold tabular text-slate-900">
              {s.count.toLocaleString()}
              <span className="ml-1.5 font-bold text-slate-400">
                ({formatShare(s.pct)})
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
